import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { WelcomeBanner } from "../../../components/admin/u1/dashboard/WelcomeBanner";
import { FollowedCompanies } from "../../../components/admin/u1/dashboard/FollowedCompanies";
import { PurchasedCompanies } from "../../../components/admin/u1/dashboard/PurchasedCompanies";
import { RecentActivities } from "../../../components/admin/u1/dashboard/RecentActivities";
import { StatusCard } from "../../../components/admin/static/u1/StatusCard";
import { OwnerCard } from "../../../components/common/static/u1/OwnerCard";
import { ResearcherCard } from "../../../components/common/static/u1/ResearcherCard";
import { VerificationBanner } from "../../../components/admin/Dashboard/dashboard/VerificationBanner";
import {
  showVerificationBanner,
  hasLinkedCompany,
} from "../../../components/admin/u1/dashboard/dashboardBanners";
import { useGetUserSubscriptionListQuery } from "../../../store/Company/companyApiSlice";
import "../../../components/common/u1/tokens.css";

/* u1 dashboard home — TIP-631.
 *
 * Visual uplift only. Every widget's data handling is transcribed in the
 * components it renders, and the Verified nudge below keeps its existing
 * condition and destination unchanged.
 *
 * The design's grid: a fluid main column beside a 366px rail, 22px gap,
 * collapsing to one column on narrow screens.
 */
export default function Dashboard() {
  const { userInfo, token } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  /* TIP-464, transcribed: the banner nudges users without an active Verified
     Company subscription, and clicking it lands on billing. */
  const { data: subscriptionData } = useGetUserSubscriptionListQuery({
    individualID: userInfo?.individual?.[0]?.individualID,
    token,
  });
  const hasVerifiedSub = Array.isArray(subscriptionData)
    ? subscriptionData.some(
        (s) => s?.subscription_type === "verified" && s?.status === "active"
      )
    : false;

  /* Both halves of the rule — see dashboardBanners. An account with no company
     linked to it has nothing to verify, so the nudge (and its trip to billing)
     is not shown to them. */
  const verificationBanner = showVerificationBanner({
    userInfo,
    hasVerifiedSubscription: hasVerifiedSub,
  });

  /* The same signal the banner above uses. A registered account with nothing
     linked to it is asked to claim a business, not to buy a membership for a
     profile it does not have. */
  const hasCompany = hasLinkedCompany(userInfo);

  const goToBilling = () => navigate("/dashboard/billing");

  return (
    <div className="u1-root grid grid-cols-1 items-start mt-6 gap-[22px] pb-6 lg:grid-cols-[minmax(0,1fr)_366px]">
      <div className="flex min-w-0 flex-col gap-[18px]">
        {verificationBanner && <VerificationBanner onClick={goToBilling} />}
        <WelcomeBanner />
        <StatusCard hasCompany={hasCompany} />
        <FollowedCompanies />
        <PurchasedCompanies />
      </div>

      <aside className="flex min-w-0 flex-col gap-[18px]">
        <OwnerCard />
        <ResearcherCard />
        <RecentActivities />
      </aside>
    </div>
  );
}
